import { useMemo, useRef, useState } from 'react';
import type { LayerSpecification } from 'mapbox-gl';
import {
  UMAppShell,
  UMBuildingDetailPanel,
  UMFiltersPanel,
  UMLayersPanel,
  UMMap,
  UMMapControls,
  UMMapToolbar,
  UMMessage,
  UMR,
  UM_DEMO_BUILDING,
  UM_LAYER_CATALOG,
  UM_RAIL_ITEMS,
  type UMBasemapId,
  type UMFilterValues,
  type UMMapHandle,
  type UMRailItemId,
} from '../_components';
import { NEW_FIELD_COUNT, SECTIONS_METIER, SECTIONS_SOURCE } from './catalog';
import { BUILDINGS, BUILDINGS_GEOJSON } from './data';
import { FiltersPanel } from './FiltersPanel';
import { ORGA_VARIANTS, RECAP_VARIANTS, RESET_VARIANTS, useOrga, useRecap, useReset } from './hooks';
import { buildingMatches } from './matching';
import type { FilterValues, VariantSpec } from './types';

export const meta = {
  title: 'Filtres des bâtiments — 46 nouveaux critères',
  description:
    'Panneau de filtres enrichi (caractéristiques, chaud / froid, données déclarées, activité ENGIE, offres CRM) : organisation par question métier ou par catégorie source, recherche de filtre, puces actives, carte filtrée.',
  tags: ['map', 'building', 'prospection'],
  author: 'Prototyper',
  status: 'draft' as const,
};

const LYON_BELLECOUR: [number, number] = [4.832, 45.7578];
/** Leaves room for the 300px filter panel on the left. */
const BUILDINGS_BOUNDS: [[number, number], [number, number]] = [[4.8195, 45.7515], [4.8395, 45.7625]];

function VariantGroup<T extends string>({ title, specs, value, onChange }: { title: string; specs: VariantSpec<T>[]; value: T; onChange: (v: T) => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: UMR.textLabel, letterSpacing: 0.4 }}>{title}</p>
      {specs.map((v) => (
        <button
          key={v.id}
          type="button"
          aria-pressed={v.id === value}
          title={v.note}
          onClick={() => onChange(v.id)}
          style={{
            textAlign: 'left', padding: '4px 8px', cursor: 'pointer', fontFamily: UMR.font, fontSize: 13,
            border: `1px solid ${v.id === value ? UMR.blue : 'transparent'}`,
            background: v.id === value ? UMR.bluePressed : 'transparent', color: UMR.black,
          }}
        >
          {v.label}
        </button>
      ))}
    </div>
  );
}

export default function FiltresBatimentsPage() {
  const mapRef = useRef<UMMapHandle>(null);
  const [panel, setPanel] = useState<UMRailItemId | null>('filters');
  const [search, setSearch] = useState('');
  const [basemap, setBasemap] = useState<UMBasemapId>('light');
  const [is3d, setIs3d] = useState(false);
  const [mode, setMode] = useState<'mapbox' | 'offline'>('offline');
  const [checked, setChecked] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(UM_LAYER_CATALOG.map((g) => [g.id, !!g.defaultChecked])),
  );
  const [filters, setFilters] = useState<FilterValues>({});
  const [buildingId, setBuildingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [studioOpen, setStudioOpen] = useState(true);
  const [orga, setOrga] = useOrga();
  const [recap, setRecap] = useRecap();
  const [reset, setReset] = useReset();

  const matching = useMemo(() => BUILDINGS.filter((b) => buildingMatches(b.attrs, filters)).map((b) => b.id), [filters]);

  const layers = useMemo<LayerSpecification[]>(
    () => [
      {
        id: 'urbanmap-building-fill',
        type: 'fill',
        source: 'buildings',
        filter: ['in', ['get', 'id'], ['literal', matching]],
        paint: {
          'fill-color': ['step', ['get', 'heat'], UMR.map.consumption[0], 300, UMR.map.consumption[1], 600, UMR.map.consumption[2], 1000, UMR.map.consumption[3]],
          'fill-opacity': 0.8,
        },
      },
      {
        id: 'urbanmap-building-picked',
        type: 'line',
        source: 'buildings',
        filter: ['==', ['get', 'id'], buildingId ?? ''],
        paint: { 'line-color': UMR.map.picked, 'line-width': 3 },
      },
    ],
    [matching, buildingId],
  );

  const toggle = (id: UMRailItemId) => setPanel((current) => (current === id || ['admin', 'contact', 'logout'].includes(id) ? null : id));
  const close = () => setPanel(null);
  const flash = (msg: string) => { setNotice(msg); window.setTimeout(() => setNotice(null), 3500); };
  const changeOrga = (v: typeof orga) => { setOrga(v); setFilters({}); };

  const picked = buildingId ? BUILDINGS.find((b) => b.id === buildingId) : undefined;
  const building = picked && {
    ...UM_DEMO_BUILDING,
    functionalId: picked.id,
    address: `${picked.name} — place Bellecour 69002 Lyon`,
    selections: [],
    heating: { ...UM_DEMO_BUILDING.heating, consumption: Math.round(picked.heat * 0.8) },
    hotWater: { ...UM_DEMO_BUILDING.hotWater, consumption: Math.round(picked.heat * 0.2) },
  };

  const filtersPanel =
    orga === 'actuel' ? (
      <UMFiltersPanel onClose={close} values={filters as UMFilterValues} onChange={(v) => setFilters(v as FilterValues)} />
    ) : (
      <FiltersPanel
        sections={orga === 'metier' ? SECTIONS_METIER : SECTIONS_SOURCE}
        useFullLabels={orga === 'source'}
        values={filters}
        onChange={setFilters}
        onClose={close}
        showChips={recap === 'puces'}
        reset={reset}
      />
    );

  return (
    <UMAppShell
      title="UrbanMap — filtres des bâtiments enrichis"
      railItems={UM_RAIL_ITEMS}
      activeRailId={panel}
      onRailSelect={toggle}
      toolbar={
        <UMMapToolbar
          search={search}
          onSearchChange={setSearch}
          selections={[]}
          selectionLabel=""
          onSelectionChange={() => undefined}
          onCreateFolder={() => flash('Création de sélection (mock)')}
          onCenterSelection={() => mapRef.current?.flyTo(LYON_BELLECOUR, 16)}
          onSelectionActions={() => flash('Actions de la sélection (mock)')}
          onLocate={() => mapRef.current?.flyTo(LYON_BELLECOUR, 16)}
        />
      }
      leftPanel={
        (panel === 'filters' && filtersPanel) ||
        (panel === 'layers' && (
          <UMLayersPanel onClose={close} checked={checked} onCheckedChange={(id, value) => setChecked((prev) => ({ ...prev, [id]: value }))} />
        )) ||
        undefined
      }
      rightPanel={
        building && (
          <UMBuildingDetailPanel
            building={building}
            onClose={() => setBuildingId(null)}
            onEdit={() => flash('Édition du bâtiment (mock)')}
            onExpand={() => flash('Vue détaillée du bâtiment (mock)')}
            availableSelections={[]}
            onSelectionsChange={() => undefined}
            onStreetView={() => flash('Street View (mock)')}
            onEarth={() => flash('Google Earth (mock)')}
          />
        )
      }
    >
      <UMMap
        ref={mapRef}
        basemap={basemap}
        is3d={is3d}
        initialBounds={BUILDINGS_BOUNDS}
        sources={{ buildings: BUILDINGS_GEOJSON }}
        layers={layers}
        interactiveLayerIds={['urbanmap-building-fill']}
        onFeatureClick={(_, f) => setBuildingId(String(f.properties?.id))}
        onModeChange={setMode}
      />
      <UMMapControls
        basemap={basemap}
        availableBasemaps={mode === 'mapbox' ? ['light', 'streets', 'satellite', 'outdoors'] : ['light']}
        onBasemapChange={setBasemap}
        onZoomIn={() => mapRef.current?.zoomIn()}
        onZoomOut={() => mapRef.current?.zoomOut()}
        is3d={is3d}
        onToggle3d={setIs3d}
      />

      <div
        style={{
          position: 'absolute', bottom: 40, right: 16, zIndex: 30, width: 280, background: UMR.white,
          border: `1px solid ${UMR.border}`, boxShadow: 'rgba(0,0,0,0.1) 0 2px 8px', fontFamily: UMR.font,
        }}
      >
        <button
          type="button"
          aria-expanded={studioOpen}
          onClick={() => setStudioOpen((o) => !o)}
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', padding: '8px 12px', border: 'none',
            borderBottom: studioOpen ? `1px solid ${UMR.border}` : 'none', background: 'transparent', cursor: 'pointer',
            fontFamily: UMR.font, fontSize: 12, fontWeight: 700, color: UMR.textLabel, letterSpacing: 0.4,
          }}
        >
          STUDIO — COMPARER LES VARIANTES
          <span aria-hidden="true">{studioOpen ? '▾' : '▸'}</span>
        </button>
        {studioOpen && (
          <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <VariantGroup title="H1 · ORGANISATION" specs={ORGA_VARIANTS} value={orga} onChange={changeOrga} />
            {orga !== 'actuel' && (
              <>
                <VariantGroup title="H3 · FILTRES ACTIFS" specs={RECAP_VARIANTS} value={recap} onChange={setRecap} />
                <VariantGroup title="H5 · RÉINITIALISER" specs={RESET_VARIANTS} value={reset} onChange={setReset} />
              </>
            )}
            <p style={{ margin: 0, fontSize: 12, lineHeight: '16px', color: UMR.textSecondary }}>
              {ORGA_VARIANTS.find((v) => v.id === orga)!.note} {orga !== 'actuel' && `${NEW_FIELD_COUNT} champs ajoutés (icône « new ») : 46 attributs + « Présent dans une offre » + année des données déclarées.`}
              {' '}Carte : {matching.length} / {BUILDINGS.length} bâtiments fictifs. Listes CRM fictives.
            </p>
          </div>
        )}
      </div>

      {notice && (
        <div style={{ position: 'absolute', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 30 }}>
          <UMMessage>{notice}</UMMessage>
        </div>
      )}
    </UMAppShell>
  );
}
